import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
    Activity,
    ArrowLeft,
    ArrowRight,
    ChevronRight,
    MessagesSquare,
    Search,
    ShieldCheck,
    Wrench,
} from 'lucide-react'
import { AppShell } from '~/components/layout/app-shell'
import { useUiStore } from '~/stores/ui-store'
import { cn } from '~/lib/utils'

// hidden 카드는 기본 숨김 — 푸터 저작권 7연속 클릭(이스터에그)으로 노출된다.
const SERVICES = [
    {
        to: '/certification',
        eyebrow: '인증 준비',
        title: 'HACCP 모의 평가 AI',
        description:
            '대화형 설문으로 준비도를 진단하고, 항목별 채점과 AI 보고서를 받아요.',
        Icon: ShieldCheck,
        iconClass: 'text-primary bg-accent',
        badgeClass: 'bg-accent text-primary',
        steps: ['설문', '채점', '보고서'],
    },
    {
        to: '/consultation',
        eyebrow: '규정 상담',
        title: 'HACCP AI 상담챗봇',
        description:
            '규모 판정·업종 식별 등 스킬로, 고시·매뉴얼 근거와 함께 답해요.',
        Icon: MessagesSquare,
        iconClass: 'text-success bg-success-subtle',
        badgeClass: 'bg-success-subtle text-success',
        steps: ['규모 판정', '업종 식별', '근거 확인'],
    },
    {
        to: '/explore',
        eyebrow: '항목 탐색',
        title: 'HACCP 체크리스트 찾아보기',
        description:
            '규모·업종·분류별 평가 항목과 기준·관리·현장 근거를 확인해요.',
        Icon: Search,
        iconClass: 'text-info bg-info-subtle',
        badgeClass: 'bg-info-subtle text-info',
        steps: ['분류 선택', '항목 검색', '근거 보기'],
    },
    {
        to: '/maintenance',
        eyebrow: '변경 관리',
        title: 'HACCP 변경 검토 AI',
        description:
            '공정·설비 변경의 영향을 분석하고 재점검·보완을 권고받아요.',
        Icon: Wrench,
        iconClass: 'text-point bg-point-subtle',
        badgeClass: 'bg-point-subtle text-point',
        steps: ['변경 분석', '영향 항목', '재점검'],
        hidden: true,
    },
    {
        to: '/monitoring',
        eyebrow: '정량지표 점검',
        title: 'HACCP 모니터링 AI',
        description:
            '업장 측정값을 기준과 대조해 HACCP 기준 이탈을 짚어 드려요.',
        Icon: Activity,
        iconClass: 'text-warning bg-warning-subtle',
        badgeClass: 'bg-warning-subtle text-warning',
        steps: ['지표 도출', '이탈 점검', 'AI 어시스턴트'],
        hidden: true,
    },
]

/** HERO 검색창 동적 placeholder로 순환할 질문 예시. */
const SEARCH_PLACEHOLDERS = [
    '우리 매장이 소규모인지 일반인지 모르겠어요',
    '선행요건이 무슨 뜻이에요?',
    '냉장 보관 온도 기준이 궁금해요',
    '우리 업종이 뭔지 찾아주세요',
    '인증 받을 수 있을지 봐주세요',
]

/** 홈. 가로 2페이지 전환(검색 집중 ↔ 5개 서비스). */
export function HomePage() {
    const navigate = useNavigate()
    const hiddenServicesRevealed = useUiStore((s) => s.hiddenServicesRevealed)
    const services = SERVICES.filter(
        (s) => !s.hidden || hiddenServicesRevealed
    )
    const [query, setQuery] = useState('')
    const [focused, setFocused] = useState(false)
    const [placeholderIndex, setPlaceholderIndex] = useState(0)
    const [page, setPage] = useState<0 | 1>(0)

    // 0/1로 클램프하며 페이지 이동.
    function goTo(next: number) {
        setPage(next <= 0 ? 0 : 1)
    }

    // 입력에 포커스되거나 값이 있으면 동적 placeholder를 멈춘다.
    const dynamicActive = !focused && query.length === 0

    useEffect(() => {
        if (!dynamicActive) return
        const id = window.setInterval(() => {
            setPlaceholderIndex((i) => (i + 1) % SEARCH_PLACEHOLDERS.length)
        }, 2500)
        return () => window.clearInterval(id)
    }, [dynamicActive])

    /** 값이 있으면 상담챗봇으로 seed를 넘긴다. (실제 경로로 직행해 리다이렉트가
     *  쿼리를 떨구지 않도록 한다: /consultation/chat?seed=...) */
    function submitSearch() {
        const seed = query.trim()
        if (!seed) return
        navigate(`/consultation/chat?seed=${encodeURIComponent(seed)}`)
    }

    // 키보드 좌우 화살표로 페이지 전환. (input/textarea 포커스 중이면 스킵)
    useEffect(() => {
        function onKeyDown(e: KeyboardEvent) {
            const el = document.activeElement
            const tag = el?.tagName.toLowerCase()
            if (tag === 'input' || tag === 'textarea') return
            if (e.key === 'ArrowRight') goTo(1)
            else if (e.key === 'ArrowLeft') goTo(0)
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [])

    // 휠 스크롤로 페이지 전환. 세로 스크롤 방지(passive:false) + 쿨다운 디바운스.
    const containerRef = useRef<HTMLDivElement | null>(null)
    const lastWheelRef = useRef(0)
    useEffect(() => {
        const node = containerRef.current
        if (!node) return
        function onWheel(e: WheelEvent) {
            e.preventDefault()
            const now = Date.now()
            if (now - lastWheelRef.current < 700) return
            if (Math.abs(e.deltaY) < 4) return
            lastWheelRef.current = now
            if (e.deltaY > 0) goTo(1)
            else if (e.deltaY < 0) goTo(0)
        }
        node.addEventListener('wheel', onWheel, { passive: false })
        return () => node.removeEventListener('wheel', onWheel)
    }, [])

    return (
        <AppShell>
            <div ref={containerRef} className='h-full w-full overflow-hidden'>
                {/* 가로 트랙: 2페이지가 이어져 translateX로 전환 */}
                <div
                    className='flex h-full flex-row transition-transform duration-500 ease-out'
                    style={{ transform: `translateX(-${page * 100}%)` }}>
                    {/* 페이지 0 — 검색 집중 */}
                    <section className='flex h-full w-full shrink-0 flex-col items-center justify-center px-4 lg:px-8'>
                        <div className='mx-auto w-full max-w-3xl'>
                            <div className='animate-in fade-in slide-in-from-bottom-2 flex items-center justify-center gap-2 duration-500'>
                                <img
                                    src='/assets/home/haccp-certification-mark.avif'
                                    alt='HACCP 인증 마크'
                                    className='size-9 object-contain'
                                />
                                <p className='text-primary text-sm font-bold tracking-[0.12em] uppercase'>
                                    식품안전 인증 지원 서비스
                                </p>
                            </div>
                            <h1 className='text-foreground animate-in fade-in slide-in-from-bottom-2 mt-5 text-center text-4xl font-extrabold tracking-tight duration-500 lg:text-5xl'>
                                HACCP, AI와 함께 준비하세요
                            </h1>
                            <p className='text-muted-foreground animate-in fade-in slide-in-from-bottom-2 fill-mode-both mx-auto mt-4 max-w-xl text-center text-base text-balance duration-500 [animation-delay:120ms] lg:text-lg'>
                                모의 평가부터 상담, 체크리스트 찾아보기까지 한
                                곳에서. 식품안전 인증을 돕는 AI 도우미입니다.
                            </p>

                            {/* 아주 큰 검색 입력 — 상담챗봇 seed 진입. */}
                            <div className='animate-in fade-in slide-in-from-bottom-2 fill-mode-both mt-10 duration-500 [animation-delay:200ms]'>
                                <div
                                    className={cn(
                                        'border-border bg-card flex items-center gap-4 rounded-3xl border px-6 shadow-lg transition-all duration-300',
                                        focused
                                            ? 'ring-primary/40 border-primary/40 shadow-xl ring-2'
                                            : 'hover:shadow-xl'
                                    )}>
                                    <Search className='text-muted-foreground size-6 shrink-0' />
                                    <input
                                        type='text'
                                        value={query}
                                        onChange={(e) =>
                                            setQuery(e.target.value)
                                        }
                                        onFocus={() => setFocused(true)}
                                        onBlur={() => setFocused(false)}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter') {
                                                e.preventDefault()
                                                submitSearch()
                                            }
                                        }}
                                        placeholder={
                                            dynamicActive
                                                ? SEARCH_PLACEHOLDERS[
                                                      placeholderIndex
                                                  ]
                                                : ''
                                        }
                                        aria-label='HACCP 무엇이든 물어보세요'
                                        className={cn(
                                            'text-foreground h-18 flex-1 bg-transparent text-lg transition-opacity duration-300 outline-none',
                                            'placeholder:text-muted-foreground/70',
                                            dynamicActive
                                                ? 'placeholder:opacity-100'
                                                : 'placeholder:opacity-0'
                                        )}
                                    />
                                    <button
                                        type='button'
                                        onClick={submitSearch}
                                        disabled={query.trim().length === 0}
                                        aria-label='상담 시작'
                                        className='bg-primary text-primary-foreground grid size-12 shrink-0 place-items-center rounded-2xl transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 disabled:hover:shadow-none'>
                                        <ArrowRight className='size-6' />
                                    </button>
                                </div>
                            </div>

                            {/* 페이지 전환 힌트 — 동적으로 움직이는 오른쪽 화살표 */}
                            <div className='mt-12 flex justify-center'>
                                <button
                                    type='button'
                                    onClick={() => goTo(1)}
                                    className='text-muted-foreground hover:text-foreground group flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors'>
                                    <span>서비스 둘러보기</span>
                                    <ChevronRight className='size-5 [animation:nudge-x_1s_ease-in-out_infinite] group-hover:translate-x-0.5' />
                                </button>
                            </div>
                        </div>
                    </section>

                    {/* 페이지 1 — 5개 서비스 wide */}
                    <section className='flex h-full w-full shrink-0 flex-col px-4 py-8 lg:px-10'>
                        <div className='flex items-center justify-between gap-4'>
                            <button
                                type='button'
                                onClick={() => goTo(0)}
                                className='text-muted-foreground hover:text-foreground group flex items-center gap-2 rounded-full px-3 py-2 text-sm font-semibold transition-colors'>
                                <ArrowLeft className='size-5 transition-transform group-hover:-translate-x-0.5' />
                                <span>검색으로</span>
                            </button>
                            <h2 className='text-foreground text-lg font-semibold'>
                                어떤 도움이 필요하세요?
                            </h2>
                        </div>
                        {/* 카드 블록을 페이지 중앙에 세로 정렬 — my-auto로 콘텐츠 높이만큼만(페이지 전체로 늘어나지 않게).
                            items-stretch로 같은 행의 카드들이 같은 높이가 되고, 각 카드는 h-full로 셀을 꽉 채운다. */}
                        <div
                            className={cn(
                                'mx-auto my-auto grid w-full max-w-screen-2xl grid-cols-1 items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3',
                                // 숨김 서비스 노출 시 5장이라 5열, 기본 3장은 3열로 꽉 차게.
                                services.length > 3 && 'xl:grid-cols-5'
                            )}>
                            {services.map((s) => (
                                <Link
                                    key={s.to}
                                    to={s.to}
                                    className='group border-border bg-card flex h-full min-h-[26rem] flex-col rounded-xl border p-8 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg'>
                                    <div className='flex items-start justify-between gap-3'>
                                        <span
                                            className={cn(
                                                'inline-flex size-14 items-center justify-center rounded-lg',
                                                s.iconClass
                                            )}>
                                            <s.Icon className='size-8' />
                                        </span>
                                        <span
                                            className={cn(
                                                'rounded-sm px-2.5 py-1 text-xs font-bold',
                                                s.badgeClass
                                            )}>
                                            {s.eyebrow}
                                        </span>
                                    </div>

                                    <div className='mt-6'>
                                        <h3 className='text-foreground text-2xl font-bold tracking-tight'>
                                            {s.title}
                                        </h3>
                                        <p className='text-muted-foreground mt-3 text-base leading-7'>
                                            {s.description}
                                        </p>
                                    </div>

                                    <div className='mt-auto flex flex-wrap gap-2 pt-6'>
                                        {s.steps.map((step) => (
                                            <span
                                                key={step}
                                                className='border-border bg-background text-muted-foreground rounded-sm border px-2 py-1 text-xs font-medium'>
                                                {step}
                                            </span>
                                        ))}
                                    </div>

                                    <div className='border-border mt-6 flex items-center justify-between border-t pt-5'>
                                        <span className='text-primary text-sm font-bold'>
                                            시작하기
                                        </span>
                                        <span className='bg-primary text-primary-foreground grid size-8 place-items-center rounded-sm transition-transform group-hover:translate-x-1'>
                                            <ArrowRight className='size-4' />
                                        </span>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </AppShell>
    )
}
